import { Navigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"

import { useAuth } from "@/lib/auth"
import { t } from "@/lib/i18n"

import { fetchPasos } from "@/routes/onboarding/api"

/**
 * A DÓNDE SE ENTRA DESPUÉS DE INICIAR SESIÓN.
 *
 * Cesar: «debería de ser que una vez te logueas sólo te abre la parte de
 * primeros pasos». Hasta ahora se entraba siempre al Dashboard, que para un
 * cliente recién llegado son diez tarjetas en cero y cuatro gráficos vacíos: la
 * peor primera impresión posible, y además no dice qué hacer.
 *
 * ===========================================================================
 * LA DECISIÓN SE TOMA CON UN DATO, NO CON UNA MARCA
 * ===========================================================================
 *
 * `pendientes_activos` — los pasos que no están listos y que nadie apartó — lo
 * calcula el backend DERIVÁNDOLO de los datos que hay en la base. No es una
 * casilla de «ya hice el onboarding» que alguien pudo marcar sin haberlo hecho,
 * que es lo que convierte estas pantallas en un trámite.
 *
 * Y cuenta los ACTIVOS, no los que faltan: un paso apartado a conciencia —«los
 * saldos de apertura los traemos en enero»— no puede seguir secuestrando la
 * entrada del producto todos los días. Apartar algo es una decisión; ignorarla
 * sería castigar a quien la tomó.
 *
 * ---------------------------------------------------------------------------
 * NO SE DECIDE EN EL LOGIN, Y ES DELIBERADO
 *
 * La página de login no sabe todavía qué cliente hay ni puede preguntar por sus
 * pasos: el perfil y la cartera llegan después de autenticarse. Ponerlo ahí
 * obligaría a encadenar dos esperas antes de pintar nada. Aquí, en la raíz, ya
 * hay sesión y tenant, y esta es la única consulta que falta.
 *
 * ---------------------------------------------------------------------------
 * SI LA CONSULTA FALLA, SE ENTRA AL PRODUCTO
 *
 * No al traspaso. Un error de red no es una prueba de que falte nada, y mandar a
 * alguien con el traspaso terminado a rellenar un cuestionario porque una
 * petición se cayó es peor que el fallo original. El dashboard, en cambio, dice
 * por sí mismo cuando no hay datos.
 */
export function Entrada() {
  const { activeTenantId, profile, loading } = useAuth()

  /*
    ESPERAR A QUE HAYA CLIENTE, Y ESTO COSTÓ UNA CAPTURA DE PANTALLA.

    La primera versión hacía `if (!activeTenantId) return <Navigate to="/dashboard">`
    y se veía perfecta: entrabas y aparecía el dashboard. El problema es que
    aparecía SIEMPRE, también con siete pasos del traspaso sin contestar.

    `activeTenantId` no viene con la sesión: lo DERIVA un efecto a partir de
    `profile` (ver `decidirTenant` en `lib/auth.tsx`). Así que hay un render en
    el que ya hay perfil y todavía no hay cliente — y justo en ese render este
    componente decidía. Un `null` que significa «aún no lo sé» se estaba leyendo
    como «no hay ninguno».

    Es el defecto de esta casa: no da error, la pantalla que sale es una pantalla
    legítima, y lo único que falla es que la decisión era otra.

    Así que hay tres esperas y sólo la última decide:
      - la sesión todavía resolviéndose (`loading`);
      - hay perfil con cartera pero el cliente aún no se ha derivado;
      - y ya con cliente, la consulta de los pasos.
  */
  const esperandoCliente =
    loading || (Boolean(profile?.memberships.length) && !activeTenantId)

  const pasos = useQuery({
    queryKey: ["onboarding-pasos", activeTenantId],
    queryFn: () => fetchPasos(),
    enabled: Boolean(activeTenantId),
    // Una sola vez: esto bloquea la entrada, y tres reintentos con espera
    // dejarían la pantalla en el spinner varios segundos por un 500 que ya
    // sabemos resolver yendo al dashboard.
    retry: false,
  })

  // Sin cartera NINGUNA —que es distinto de «todavía no sé cuál»— no hay nada
  // que preguntar. `AppLayout` ya sabe decir que no hay clientes; repetirlo aquí
  // sería un segundo sitio donde mantener el mismo mensaje.
  if (!esperandoCliente && !activeTenantId) return <Navigate to="/dashboard" replace />

  if (esperandoCliente || pasos.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <p className="flex items-center gap-2 text-2xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t("Viendo por dónde vas…")}
        </p>
      </div>
    )
  }

  const alTraspaso = Boolean(pasos.data && pasos.data.pendientes_activos > 0)
  return <Navigate to={alTraspaso ? "/primeros-pasos" : "/dashboard"} replace />
}
